import { MenuItem, Select } from "@mui/material";
import AuthContext from "context/AuthContext";
import useToastContext from "hooks/useToastContext";
import { isEmpty } from "lodash";
import Fab from "pages/test_manager/components/Fab";
import Pagination from "pages/test_manager/components/Pagination";
import QuestionDialog from "pages/test_manager/components/QuestionDialog";
import useQuizFetch from "pages/test_manager/hooks/useQuizFetch";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TiPencil, TiPlus, TiTrash } from "react-icons/ti";
import { Link, useSearchParams } from "react-router-dom";
import { apiDelete, apiGet, apiPost, apiPut } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import helpers from "utils/helpers";
const path = apiPath.quizQuestions;

function QuestionsPage() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const permissions = user?.permission?.find(
    (e) => e.manager === "test_manager",
  );
  const { fetchQuestionBanks, fetchTests, fetchQuestions } = useQuizFetch();
  const notification = useToastContext();
  const [params] = useSearchParams();
  const testId = params.get("testId");
  const questionBankId = params.get("questionBankId");
  const [questions, setQuestions] = useState([]);
  const [tests, setTests] = useState();
  const [questionBanks, setQuestionBanks] = useState();
  const [createQuestionOpen, setCreateQuestionOpen] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(testId || "");
  const [selectedQuestionBankId, setSelectedQuestionBankId] = useState(
    questionBankId || "",
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    async function getData() {
      const questions = await fetchQuestions({
        pageSize: 10,
        page: page,
        testId: selectedTestId,
        questionBankId: selectedQuestionBankId,
      });
      setTotalPages(questions?.results?.totalPages);
      setQuestions(questions?.results?.docs);
      const tests = await fetchTests();
      setTests(tests?.results?.docs);
      const questionBanks = await fetchQuestionBanks();
      setQuestionBanks(questionBanks?.results?.docs);
    }
    getData();
  }, [
    fetchQuestions,
    fetchTests,
    fetchQuestionBanks,
    page,
    selectedTestId,
    selectedQuestionBankId,
  ]);

  const [question, setQuestion] = useState(null);
  async function deleteQuestionHandler(id) {
    try {
      const path = apiPath.quizQuestions + "/" + id;
      const result = await apiDelete(path);
      if (result?.status === 200) {
        notification.success(t("O_QUESTION_DELETED_SUCCESSFULLY"));
        const questions = await fetchQuestions({ pageSize: 10, page: page });
        setQuestions(questions?.results?.docs);
        setTotalPages(questions?.results?.totalPages);
      }
    } catch (error) {
      notification.error(error?.response?.data?.message);
      console.log(error);
    }
  }
  async function editQuestionHandler(id) {
    try {
      const path = apiPath.quizQuestions + "/" + id;
      const { data } = await apiGet(path);
      setQuestion(data?.results);
      setCreateQuestionOpen(false);
      const questions = await fetchQuestions({ pageSize: 10, page: page });
      setQuestions(questions?.results?.docs);
      setTotalPages(questions?.results?.totalPages);
    } catch (error) {
      notification.error(error?.response?.data?.message);
      console.log(error);
    }
  }

  async function questionUpdatedHandler(question) {
    if (!question.testId) delete question.testId;
    if (!question.questionBankId) delete question.questionBankId;
    try {
      const path = apiPath.quizQuestions + "/" + question._id;
      const { data } = await apiPut(path, question);
      if (data?.success) {
        notification.success(t("O_QUESTION_UPDATED_SUCCESSFULLY"));
        setQuestion(null);
        const questions = await fetchQuestions({ pageSize: 10, page: page });
        setQuestions(questions?.results?.docs);
        setTotalPages(questions?.results?.totalPages);
      }
    } catch (error) {
      notification.error(error?.response?.data?.message);
      console.log(error);
    }
  }

  async function questionAddedHandler(question) {
    if (!question.testId) delete question.testId;
    if (!question.questionBankId) delete question.questionBankId;
    try {
      const path = apiPath.quizQuestions;
      const { data } = await apiPost(path, question);
      if (data?.success)
        notification.success(t("O_QUESTION_CREATED_SUCCESSFULLY"));
      setCreateQuestionOpen(false);
      const questions = await fetchQuestions({ pageSize: 10, page: page });
      setQuestions(questions?.results?.docs);
      setTotalPages(questions?.results?.totalPages);
    } catch (error) {
      notification.error(error?.response?.data?.message);
    }
  }

  return (
    <div className="bg-white px-2 py-4 flex flex-col gap-4">
      <h1 className="text-2xl font-bold">{t("O_QUESTIONS")}</h1>
      <hr className="my-2" />
      <div className="flex gap-4">
        <div className="flex-1">
          <p className="">Tests</p>
          <Select
            variant="outlined"
            size="small"
            className="w-full"
            onChange={(e) => setSelectedTestId(e.target.value)}
            value={selectedTestId}
            label={t("O_TEST")}
          >
            <MenuItem key="All" value="">
              All
            </MenuItem>
            {tests &&
              tests?.map((test) => (
                <MenuItem key={test._id} value={test._id}>
                  {test.title}
                </MenuItem>
              ))}
          </Select>
        </div>
        <div className="flex-1">
          <p className="">Question Banks</p>
          <Select
            variant="outlined"
            size="small"
            className="w-full"
            onChange={(e) => setSelectedQuestionBankId(e.target.value)}
            value={selectedQuestionBankId}
            label={t("O_TEST")}
          >
            <MenuItem key="All" value="">
              All
            </MenuItem>
            {questionBanks &&
              questionBanks?.map((test) => (
                <MenuItem key={test._id} value={test._id}>
                  {test.title}
                </MenuItem>
              ))}
          </Select>
        </div>
      </div>

      {(permissions?.add || user?.role === "admin") && (
        <Fab onClick={() => setCreateQuestionOpen(true)}>
          <TiPlus />
          Create Question
        </Fab>
      )}
      <div className="pt-3 overflow-x-auto relative border mb-20">
        <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400 ">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="py-3 px-6">
                #
              </th>

              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_TEST")}
              </th>
              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_QUESTION_BANK")}
              </th>

              <th scope="col" className="py-3 px-6 text-center">
                {t("O_QUESTION_TITLE")}
              </th>

              <th scope="col" className="py-3 px-6 text-center">
                {t("O_QUESTION_DESCRIPTION")}
              </th>

              <th scope="col" className="py-3 px-6">
                {t("O_TYPE")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("O_POINTS")}
              </th>

              <th scope="col" className="py-3 px-6 cursor-pointer">
                {t("O_ACTION")}
              </th>
            </tr>
          </thead>
          <tbody>
            {questions &&
              questions?.map((item, i) => (
                <tr className="border-b border-[#E1E6EE]" key={item._id}>
                  <td className="py-3 px-6 border-l">{i + 1}</td>
                  <td className="py-3 px-6 border-l">
                    {item?.testId ? (
                      <Link
                        className="text-green-400 hover:text-green-700 font-bold"
                        to={`/test-manager/tests/details/${item.testId?._id}`}
                      >
                        {item.testId?.title}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="py-3 px-6 border-l ">
                    {item?.questionBankId ? (
                      <Link
                        className="text-green-400 hover:text-green-700 font-bold"
                        to={`/test-manager/question-bank/details/${item.questionBankId?._id}`}
                      >
                        {item.questionBankId?.title}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td className="py-3 px-6 border-l">{item.title}</td>
                  <td className="py-3 px-6 border-l">{item.description}</td>
                  <td className="py-3 px-6 border-l">{item.type}</td>
                  <td className="py-3 px-6 border-l">{item.points}</td>

                  <td className="py-3 px-6 border-l">
                    <div className="flex justify-center gap-2">
                      {(permissions?.add || user?.role === "admin") && (
                        <button
                          className="bg-red-500 hover:bg-red-700 transition-colors duration-150 text-white p-1 rounded-full"
                          onClick={() =>
                            helpers.alertFunction(
                              `Are you sure want to delete ${item.title}?`,
                              item._id,
                              deleteQuestionHandler,
                              true,
                            )
                          }
                        >
                          <TiTrash size={16} />
                        </button>
                      )}
                      {(permissions?.add || user?.role === "admin") && (
                        <button
                          className="bg-blue-500 hover:bg-blue-700 transition-colors duration-150 text-white p-1 rounded-full"
                          onClick={() => editQuestionHandler(item._id)}
                        >
                          <TiPencil size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            {isEmpty(questions) && (
              <tr className="bg-white border-b w-full text-center dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6" colSpan={12}>
                  {t("O_NO_RECORD_FOUND")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        {question && (
          <QuestionDialog
            type="edit"
            questionUpdatedHandler={questionUpdatedHandler}
            open={true}
            question={question}
            closeHandler={() => setQuestion(null)}
          />
        )}
        {createQuestionOpen && (
          <QuestionDialog
            type="create"
            questionAddedHandler={questionAddedHandler}
            open={createQuestionOpen}
            closeHandler={() => setCreateQuestionOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

export default QuestionsPage;
